import { ENUM__error_type } from '@shared/asserts/ENUM__error_type';
import {
  assert_array_not_empty,
  assert_array_typeof_number,
  assert_instanceof,
  assert_is_safe_number,
  assert_type_number,
} from '@shared/asserts/utils_asserts';
import { CONSTANTS__generation_settings } from '@shared/constants/CONSTANTS__generation_settings';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { FN_SERIALIZATION__data_deserialize } from '@shared/serialization/FN_SERIALIZATION__data_deserialize';
import { MAP__field_data } from '@shared/types/types';
import { DTO__number_range } from '../../../settings/DTO__number_range';
import { DTO_GRAPH__instance } from '../../_graph/DTO_GRAPH__instance';
import { ENUM__field_connectable_type } from '../_base/_methods/ENUM__field_connectable_type';
import { DECORATOR__class_serialization_tested } from './_base/DECORATOR__class_serialization_tested';
import { DECORATOR__class_serialization_tested_for_hash } from './_base/DECORATOR__class_serialization_tested_for_hash';
import { DECORATOR__field_is_input } from './_base/DECORATOR__field_is_input';
import { DECORATOR__field_not_linked } from './_base/DECORATOR__field_not_linked';
import { FIELD_PRIMITIVE__base } from './_base/FIELD_PRIMITIVE__base';

const { SEED_RANGE } = CONSTANTS__generation_settings;

@DECORATOR__class_serialization_tested
@DECORATOR__class_serialization_tested_for_hash
export class FIELD_PRIMITIVE__seed extends FIELD_PRIMITIVE__base {
  /*
   * STATIC FIELDS
   */

  static _class_name_static = ENUM__dto_class_name.GRAPH_FIELD_PRIMITIVE_SEED;

  /*
   * STATIC METHODS
   */
  static seed_generate__random_unique(seeds_existing: number[]): number {
    let out;

    const value_random = SEED_RANGE.get_random_value();

    if (seeds_existing.some(seed => seed === value_random)) {
      out = FIELD_PRIMITIVE__seed.seed_generate__random_unique(seeds_existing);
    } else {
      out = value_random;
    }

    return out;
  }

  static seed_generate__sequential(seeds_existing: number[], seed_base: number): number {
    let value = seed_base + 1;

    // overflow fix
    value = value % SEED_RANGE.max;

    let out = value;

    // If value exists, increment and try again
    if (seeds_existing.some(seed => seed === value)) {
      out = FIELD_PRIMITIVE__seed.seed_generate__sequential(seeds_existing, value);
    }

    // overflow fix
    value = value % SEED_RANGE.max;

    return out;
  }

  /*
   * PROPERTIES
   */

  field_connectable__type(): ENUM__field_connectable_type {
    return ENUM__field_connectable_type.SEED;
  }

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  get_value() {
    assert_type_number(this.value);

    return this.value;
  }

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  set_value(value_new: number) {
    this.value = value_new;
  }

  /*
   * VALUE RANGE
   */

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  set_value_range(value_new: unknown) {
    assert_array_typeof_number(value_new);

    this.value_range = value_new;
  }

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  get_value_range() {
    assert_array_typeof_number(this.value_range);

    return this.value_range;
  }
  /*
   * VALUES ALLOWED
   */

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  get_values_allowed() {
    assert_instanceof(this.values_allowed, DTO__number_range);

    return this.values_allowed;
  }

  override get grid2d_dimension_values(): number[] {
    return this.get_value_range();
  }

  /*
   * PROPERTIES
   */

  /*
   * METHODS
   */

  constructor() {
    super();

    this.value_range = [];

    this.values_allowed = DTO__number_range.CREATE_STUB();
  }

  use_new_seeds() {
    let value_range = this.get_value_range();
    assert_array_not_empty(value_range);

    let is_last_seed_used = value_range.last() == this.get_value();

    if (this.meta.grid2d__is_dimension) {
      let seeds_new = [];

      let seeds_to_generatate_amount = value_range.length;

      for (let i = 0; i < seeds_to_generatate_amount; i++) {
        const seed_new = FIELD_PRIMITIVE__seed.seed_generate__random_unique(seeds_new);

        seeds_new.push(seed_new);
      }

      this.set_value_range(seeds_new);
      this.set_value(seeds_new[0]);
    } else if (is_last_seed_used) {
      let new_seed = FIELD_PRIMITIVE__seed.seed_generate__random_unique(value_range);

      let value_range_new = [...value_range];
      value_range_new.push(new_seed);

      this.set_value_range(value_range_new);
      this.set_value(new_seed);
    } else {
      //use next seed
      let index_seed_current = this.get_value_range().indexOf(this.get_value());
      let index_seed_next = index_seed_current + 1;

      let seed_next = this.get_value_range()[index_seed_next];
      assert_is_safe_number(seed_next);

      this.set_value(seed_next);
    }
  }
  /*
   *
   */

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  store__can_deserialize_from_data(data: MAP__field_data): boolean {
    if (!super.store__can_deserialize_from_data(data)) return false;

    let values_allowed = this.get_values_allowed();

    let values_allowed_data = data.get_or_default('values_allowed', values_allowed);
    values_allowed_data = FN_SERIALIZATION__data_deserialize(values_allowed_data);

    assert_instanceof(values_allowed_data, DTO__number_range);

    if (!values_allowed.is_number_range_is_in_range(values_allowed_data)) return false;

    return true;
  }

  /*
   * VALIDATION
   */
  override _validate__case_base(graph: DTO_GRAPH__instance) {
    super._validate__case_base(graph);

    if (this.get_values_allowed().is_stub()) this.error_add(ENUM__error_type.GRAPH_FIELD_NUMBER__VALUES_ALLOWED_IS_STUB);
  }

  override _validate__case_input(graph: DTO_GRAPH__instance) {
    super._validate__case_input(graph);

    if (this.get_value_range().is_empty()) this.error_add(ENUM__error_type.GRAPH_FIELD_SEED__VALUE_RANGE_IS_EMPTY);
    if (!this.get_values_allowed().is_number_is_in_range(this.get_value())) this.error_add(ENUM__error_type.VALIDATION_INVALID_VALUE);

    for (let value of this.get_value_range()) {
      if (this.get_values_allowed().is_number_is_in_range(value)) continue;

      this.error_add(ENUM__error_type.VALIDATION_INVALID_VALUE);
    }
  }

  /*
   * ASSERTS
   */

  assert_standard_fields(): void {
    super.assert_standard_fields();

    assert_type_number(this.value);

    assert_array_typeof_number(this.value_range);

    assert_array_not_empty(this.value_range);
  }
}
